import { cn } from "./classNames-DkX30VJU.js";
import { Tooltip } from "./utils-BxEJOnFD.js";
import { mergeRefs } from "./internal/utils/mergeRefs.js";
import { Loader2 } from "./icons-DDR9dFSr.js";
import { Fragment, createElement, isValidElement, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Root, Slottable } from "@radix-ui/react-slot";
import { cva } from "class-variance-authority";
import { jsx, jsxs } from "react/jsx-runtime";
// src/components/Button/motion.ts
const ends = (animation) => Number.isFinite(animation.effect?.getComputedTiming().endTime);
/** Runs `callback` once the finite `animations` have finished or been cancelled; returns a cancel. */
const afterAnimations = (animations, callback) => {
	let isCurrent = true;
	Promise.allSettled(animations.filter(ends).map((animation) => animation.finished)).then(() => {
		if (isCurrent) callback();
	});
	return () => {
		isCurrent = false;
	};
};
let waiting = /* @__PURE__ */ new Set();
let framed = /* @__PURE__ */ new Set();
let isTicking = false;
const tick = () => {
	for (const node of framed) if (node.isConnected) node.setAttribute("data-button-ready", "");
	framed = waiting;
	waiting = /* @__PURE__ */ new Set();
	isTicking = framed.size > 0;
	if (isTicking) requestAnimationFrame(tick);
};
/**
* Sets `data-button-ready` on `node` two frames after it mounts, arming its entry animations.
*
* - One shared frame loop serves every Button mounting at once.
* - Arming twice is a no-op, so a re-attached ref doesn't restart the countdown.
*/
const armMotion = (node) => {
	if (node.hasAttribute("data-button-ready") || waiting.has(node) || framed.has(node)) return;
	waiting.add(node);
	if (!isTicking) {
		isTicking = true;
		requestAnimationFrame(tick);
	}
};
// src/components/Button/ButtonLabel.tsx
const isText = (node) => typeof node === "string" || typeof node === "number";
/** Milliseconds from a computed `transition-duration` such as `0.3s`. */
const toMs = (duration) => parseFloat(duration) * (duration.endsWith("ms") ? 1 : 1e3);
/**
* A button's text, rolled over when it changes.
*
* - The new text rolls in; the old one stays absolutely positioned while it rolls out.
* - The width animates from the old text's to the new one's, so neighbours slide instead of jumping.
* - Motion lives in `styles.css` under `[data-slot='button-label']`.
*/
const ButtonLabel = ({ text }) => {
	const ref = useRef(null);
	const exitRef = useRef(null);
	const interruptedWidthRef = useRef(null);
	const [shown, setShown] = useState(text);
	const [exiting, setExiting] = useState(null);
	const [hasChanged, setHasChanged] = useState(false);
	if (shown !== text) {
		setShown(text);
		setExiting(shown);
		setHasChanged(true);
	}
	useLayoutEffect(() => {
		const node = ref.current;
		const from = interruptedWidthRef.current ?? exitRef.current?.offsetWidth;
		interruptedWidthRef.current = null;
		if (!node || !from || !node.closest("[data-button-ready]")) return;
		const to = node.offsetWidth;
		if (Math.abs(to - from) < .5 || window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) return;
		const { transitionDuration, transitionTimingFunction } = getComputedStyle(node);
		const animation = node.animate?.([{
			width: `${from}px`,
			overflowX: "clip",
			textAlign: "start"
		}, {
			width: `${to}px`,
			overflowX: "clip",
			textAlign: "start"
		}], {
			duration: toMs(transitionDuration),
			easing: transitionTimingFunction
		});
		return () => {
			if (animation?.playState === "running") interruptedWidthRef.current = node.offsetWidth;
			animation?.cancel();
		};
	}, [text]);
	useEffect(() => {
		const node = exitRef.current;
		if (!node) return;
		return afterAnimations(node.getAnimations?.() ?? [], () => setExiting(null));
	}, [exiting]);
	if (!hasChanged) return text;
	return /* @__PURE__ */ jsxs("span", {
		ref,
		"data-slot": "button-label",
		children: [/* @__PURE__ */ jsx("span", {
			"data-slot": "button-label-text",
			children: text
		}, text), exiting !== null && /* @__PURE__ */ jsx("span", {
			ref: exitRef,
			"aria-hidden": true,
			"data-slot": "button-label-exit",
			children: exiting
		}, exiting)]
	});
};
/**
* `children` with each run of text wrapped in a `ButtonLabel`.
*
* - Positions are kept: a joined run leaves `null` behind, so siblings keep their identity.
* - Whitespace-only text stays bare: as a span it would become a flex item and take a gap.
*/
const withLabels = (children) => {
	if (isValidElement(children) && children.type === Fragment) return withLabels(children.props.children);
	const items = Array.isArray(children) ? children : [children];
	const wrapped = items.map((item, index) => {
		if (!isText(item)) return item;
		if (index > 0 && isText(items[index - 1])) return null;
		let text = "";
		for (let i = index; i < items.length && isText(items[i]); i++) text += String(items[i]);
		return text.trim() ? /* @__PURE__ */ jsx(ButtonLabel, { text }, index) : text;
	});
	return createElement(Fragment, null, ...wrapped);
};
// src/components/Button/styles.ts
const buttonVariants = cva([
	"tailwindcss-preflight",
	"tw:relative",
	"tw:inline-flex",
	"tw:items-center",
	"tw:justify-center",
	"tw:whitespace-nowrap",
	"tw:gap-2",
	"tw:border",
	"tw:border-transparent",
	"tw:rounded-[var(--radius-lg,0.25rem)]",
	"tw:font-medium",
	"tw:select-none",
	"tw:!outline-hidden",
	"tw:focus-visible:ring-2",
	"tw:disabled:cursor-not-allowed",
	"tw:disabled:opacity-disabled",
	"tw:active:opacity-60",
	"tw:transition-all",
	"tw:!no-underline",
	"tw:aria-disabled:cursor-not-allowed",
	"tw:aria-disabled:opacity-disabled",
	"tw:shrink-0",
	"tw:[&_svg]:pointer-events-none",
	"tw:[&_svg]:shrink-0",
	"tw:[&_svg:not([class*='tw:size-'])]:size-4"
], {
	defaultVariants: {
		variant: "primary",
		size: "medium"
	},
	variants: {
		block: { true: ["tw:w-full"] },
		variant: {
			primary: [
				"tw:bg-primary",
				"tw:text-primary-foreground",
				"tw:[a]:hover:bg-primary/80",
				"tw:hover:not-disabled:bg-primary/90",
				"tw:hover:text-primary-foreground",
				"tw:focus:text-primary-foreground",
				"tw:focus-visible:border-primary",
				"tw:focus-visible:ring-ring/50"
			],
			secondary: [
				"tw:bg-primary/10",
				"tw:text-primary",
				"tw:hover:not-disabled:bg-primary/20",
				"tw:focus-visible:border-primary",
				"tw:focus-visible:ring-ring/50"
			],
			kai: [
				"tw:bg-kai",
				"tw:animate-kai-drift",
				"tw:motion-reduce:animate-none",
				"tw:text-primary-foreground",
				"tw:hover:text-primary-foreground",
				"tw:focus:text-primary-foreground",
				"tw:hover:not-disabled:brightness-110",
				"tw:focus-visible:ring-ring/50"
			],
			danger: [
				"tw:bg-destructive",
				"tw:text-destructive-foreground",
				"tw:hover:not-disabled:bg-destructive/90",
				"tw:hover:text-destructive-foreground",
				"tw:focus:text-destructive-foreground",
				"tw:focus-visible:border-destructive",
				"tw:focus-visible:ring-destructive/20"
			],
			outline: [
				"tw:!border-input",
				"tw:bg-background",
				"tw:bg-clip-padding",
				"tw:text-foreground",
				"tw:hover:not-disabled:bg-[color-mix(in_oklab,rgb(var(--primary))_10%,rgb(var(--background)))]",
				"tw:hover:not-disabled:!border-muted-foreground",
				"tw:hover:not-disabled:text-primary",
				"tw:hover:active:bg-background",
				"tw:focus-visible:!border-primary",
				"tw:focus-visible:ring-ring/50"
			],
			invisible: [
				"tw:bg-transparent",
				"tw:bg-clip-padding",
				"tw:text-foreground",
				"tw:hover:not-disabled:bg-primary/10",
				"tw:hover:not-disabled:text-primary",
				"tw:hover:not-disabled:border-primary/10",
				"tw:focus-visible:ring-ring/50"
			],
			inline: [
				"tw:bg-transparent",
				"tw:text-primary",
				"tw:hover:underline",
				"tw:hover:active:bg-transparent",
				"tw:focus-visible:ring-ring/50"
			]
		},
		size: {
			medium: [
				"tw:max-h-9",
				"tw:py-2",
				"tw:px-4",
				"tw:text-sm"
			],
			small: [
				"tw:max-h-8",
				"tw:py-2",
				"tw:px-3",
				"tw:text-xs"
			],
			large: [
				"tw:max-h-10",
				"tw:py-2.5",
				"tw:px-6",
				"tw:text-sm"
			],
			"icon-lg": ["tw:size-10"],
			icon: ["tw:size-9"],
			"icon-sm": ["tw:size-8"],
			"icon-xs": ["tw:size-6"],
			"icon-2xs": [],
			"icon-3xs": []
		}
	}
});
// src/components/Button/Button.tsx
/** The children that render anything, looking through arrays and fragments. */
const renderedChildren = (node) => {
	if (Array.isArray(node)) return node.flatMap(renderedChildren);
	if (isValidElement(node) && node.type === Fragment) return renderedChildren(node.props.children);
	return node == null || typeof node === "boolean" || typeof node === "string" && !node.trim() ? [] : [node];
};
/**
* Any user-initiated action; `size="icon-*"` with no children is the icon-only button.
*
* - Not for: inline text links (`ButtonInline`), URL navigation (`Link`), action + dropdown (`SplitButton`).
* - Wrap in `Tooltip` when icon-only; `disabledReason` when disabled; `Popconfirm` for destructive actions.
*
* @see RECIPES.md#confirm-dialog
*/
const Button = ({ ref, children, disabled = false, className, type = "button", isLoading = false, asChild = false, onClick, variant = "primary", size = "medium", block = false, disabledReason, ...buttonProps }) => {
	const Comp = asChild ? Root : "button";
	const isIconOnly = String(size).startsWith("icon");
	const classNames = buttonVariants({
		variant,
		size,
		block,
		className
	});
	const content = asChild && isValidElement(children) ? children.props.children : children;
	const rendered = renderedChildren(content);
	const leadsWithElement = isValidElement(rendered[0]);
	const elementTypes = rendered.flatMap((child) => isValidElement(child) ? [child.type] : []);
	const [prev, setPrev] = useState({
		leadsWithElement,
		elementTypes
	});
	const [spinnerEnter, setSpinnerEnter] = useState(isLoading ? "fade" : null);
	const [iconEnter, setIconEnter] = useState("fade");
	const placement = isIconOnly ? "center" : leadsWithElement ? "leading" : "inline";
	if (isLoading && !spinnerEnter) setSpinnerEnter(placement === "inline" && !prev.leadsWithElement ? "grow" : "fade");
	const typesChanged = prev.elementTypes.length !== elementTypes.length || prev.elementTypes.some((type, index) => type !== elementTypes[index]);
	if (typesChanged || prev.leadsWithElement !== leadsWithElement) setPrev({
		leadsWithElement,
		elementTypes
	});
	if (typesChanged) setIconEnter(elementTypes.length > prev.elementTypes.length && !isLoading && !spinnerEnter ? "grow" : "fade");
	const spinnerRef = useRef(null);
	useEffect(() => {
		const node = spinnerRef.current;
		if (isLoading || !node) return;
		return afterAnimations(node.getAnimations?.() ?? [], () => setSpinnerEnter(null));
	}, [isLoading]);
	useLayoutEffect(() => {
		const slot = spinnerRef.current;
		const button = slot?.offsetParent;
		if (!slot || !button || placement !== "leading") return;
		const centre = () => {
			const icon = slot.nextElementSibling;
			if (!(icon instanceof SVGElement)) return;
			const [iconBox, buttonBox] = [icon.getBoundingClientRect(), button.getBoundingClientRect()];
			const scale = buttonBox.width / (button instanceof HTMLElement ? button.offsetWidth : 1) || 1;
			const x = (iconBox.left - buttonBox.left + iconBox.width / 2) / scale - button.clientLeft;
			slot.style.left = `${x - parseFloat(getComputedStyle(slot).width) / 2}px`;
		};
		centre();
		if (typeof ResizeObserver === "undefined") return;
		const observer = new ResizeObserver(centre);
		observer.observe(button);
		return () => observer.disconnect();
	}, [placement, spinnerEnter]);
	const buttonRef = useRef(null);
	const armRef = useCallback((node) => node ? armMotion(node) : void 0, []);
	const mergedRef = useMemo(() => mergeRefs(ref, buttonRef, armRef), [ref, armRef]);
	useEffect(() => {
		const node = buttonRef.current;
		if (iconEnter !== "grow" || !node) return;
		const entries = [...node.querySelectorAll(":scope > svg")].flatMap((icon) => icon.getAnimations?.() ?? []);
		return afterAnimations(entries, () => setIconEnter("fade"));
	}, [iconEnter]);
	const hasDisabledReason = !!disabledReason;
	const handleClick = (event) => {
		if (event.currentTarget.tagName === "A" && disabled || hasDisabledReason) {
			event.preventDefault();
			return;
		}
		onClick?.(event);
	};
	const button = /* @__PURE__ */ jsxs(Comp, {
		...buttonProps,
		ref: mergedRef,
		disabled: disabled && !hasDisabledReason,
		"aria-disabled": buttonProps["aria-disabled"] ?? (disabled || hasDisabledReason),
		"data-variant": variant,
		"data-icon-only": isIconOnly,
		"data-icon-enter": iconEnter,
		type,
		onClick: handleClick,
		className: cn(classNames),
		children: [spinnerEnter && /* @__PURE__ */ jsx("span", {
			ref: spinnerRef,
			"aria-hidden": true,
			"data-slot": "button-spinner",
			"data-state": isLoading ? "open" : "closed",
			"data-placement": placement,
			"data-enter": spinnerEnter,
			children: /* @__PURE__ */ jsx(Loader2, { className: "tw:animate-spin" })
		}), /* @__PURE__ */ jsx(Slottable, { children: asChild ? children : withLabels(children) })]
	});
	if (!hasDisabledReason) return button;
	return /* @__PURE__ */ jsx(Tooltip, {
		tooltip: disabledReason,
		placement: "top",
		variant: "explanatory",
		triggerAsChild: true,
		children: button
	});
};
// src/components/Button/ButtonInline.tsx
const buttonInlineVariants = cva([
	"tailwindcss-preflight",
	"tw:inline-flex",
	"tw:items-center",
	"tw:gap-2",
	"tw:underline-offset-2",
	"tw:disabled:opacity-40",
	"tw:disabled:cursor-not-allowed",
	"tw:disabled:text-muted-foreground",
	"tw:disabled:no-underline",
	"tw:disabled:[&_svg]:text-muted-foreground",
	"tw:[&_svg.lucide:not([class*='tw:size-'])]:size-4",
	"tw:[&_svg]:shrink-0"
], {
	defaultVariants: {
		variant: "link",
		color: "link"
	},
	variants: {
		color: {
			link: [
				"tw:text-primary",
				"tw:hover:text-primary/90",
				"tw:[&_svg]:text-primary"
			],
			dark: ["tw:text-foreground tw:[&_svg]:text-muted-foreground"],
			danger: ["tw:text-error-500 tw:[&_svg]:text-error-500"],
			muted: ["tw:text-muted-foreground tw:[&_svg]:text-muted-foreground"],
			custom: []
		},
		variant: {
			link: ["tw:underline", "tw:hover:no-underline"],
			"link-reverse": [
				"tw:no-underline",
				"",
				"tw:hover:underline"
			],
			custom: []
		}
	}
});
const ButtonInline = ({ ref, children, className, variant, color, type = "button", asChild = false, ...buttonProps }) => {
	return /* @__PURE__ */ jsx(asChild ? Root : "button", {
		...buttonProps,
		ref,
		type: asChild ? void 0 : type,
		className: buttonInlineVariants({
			variant,
			color,
			className
		}),
		children
	});
};
export { Button, ButtonInline, buttonVariants };

//# sourceMappingURL=Button-BAMX1iuI.js.map